/**
 * The "Shop by category" static taxonomy (UID-253 rebuild). Ported verbatim
 * (leaf-category NAMES only — the `kind` tag on each tuple, e.g. `'shaft'`,
 * drives the interactive prototype's fake-part generator and has no
 * counterpart in our real catalog data, so it's dropped) from the design
 * source of truth's `buildCatalog()`'s `groupsRaw` constant:
 * `design_handoff_misumi_unified_platform/Fictiv Platform.dc.html` (~line
 * 6613, as of the 2026-08-24 UID-253 rebuild). 7 named groups, each listing
 * its leaf category names in the prototype's own order.
 *
 * This is a FRONTEND-ONLY constant, not a backend contract — there is no
 * `GET /catalog/taxonomy` (see ADR-0091). The real, authoritative set of
 * category labels always comes from `GET /catalog/categories`; this table
 * only says how to GROUP those real labels for the browse landing. A real
 * category whose exact name isn't listed in any group below falls into the
 * "Other" bucket at render time (`groupCategories.ts`) rather than being
 * dropped — this taxonomy was hand-authored against the prototype's example
 * data and is not guaranteed to enumerate every category our catalog carries.
 */
export interface CatalogTaxonomyGroup {
  readonly name: string
  /** Leaf category names, exact-match against `GET /catalog/categories`. */
  readonly categories: readonly string[]
}

export const CATALOG_TAXONOMY_GROUPS: readonly CatalogTaxonomyGroup[] = [
  {
    name: 'Linear Motion',
    categories: [
      'Linear Shafts',
      'Linear Ball Bearings',
      'Plain Bushings & Thrust Washers',
      'Linear Guides',
      'Linear Actuators',
      'Ball Screws',
      'Ball Screw Support Units',
      'Lead Screws & Slide Screws',
      'Shaft Supports',
      'Shaft Collars',
      'Linear Slide Assemblies',
      'Cross Rollers',
      'Ball Guides',
      'Spline Shafts',
      'Cable & Hose Carrier Systems',
      'Thrust Plates & Gibs',
      // UID-419 Automation Components batch (feat/pdp-category-captures) —
      // exact leaf names from GET /catalog/categories, cross-checked against
      // misumi-unified-apis's demo-catalog-subset.manifest.json
      // partNumberIngestPass.categories (Linear Motion batch) so newly seeded
      // categories resolve into this group instead of falling to "Other".
      'Self-Lubricating Bars & Pipes',
      'Standard Linear Guides',
      'Linear Guide Accessories',
      'Other Linear Guides',
      'Single Axis Robots',
      'Single Axis Units (Screw Driven)',
      'Belt Actuators & Accessories',
      'Lead Screw Shafts',
      'Lead Screw Clamp Plates',
      'Lead Screw Nuts',
      'Lead Screw Position Indicators',
      'Lead Screw Support Units',
      'Slide Screws',
      'Bearing Linear Rails',
      'Lubrication-Free Guide Units',
      'Roller Linear Rails',
      'Simplified Slide Packs',
      'V-Roller Assemblies',
      'Cross Roller Guides',
      'Cross Roller Tables',
      'Linear Ball Slides',
      'Cable Carriers',
      'Cable Carrier Accessories',
      'Basic Gibs',
      'Gibs',
      'Lubrication-Free Plates',
      'Thrust Plates',
      'Other Linear Motion Components',
    ],
  },
  {
    name: 'Rotary Motion',
    categories: [
      'Ball & Roller Bearings',
      'Bearing Accessories',
      'Bearing Units',
      'Bearings with Housings',
      'Cam Followers & Roller Followers',
      'Cantilever Shafts',
      'Rotary Shafts & Drive Shafts',
      'Rotary Shafts',
      // UID-419: was 'Rod End & Spherical Bearings' — the real seeded MISUMI
      // leaf name (categoryCode M2205000000) is 'Rod End Bearings &
      // Spherical Bearings'; fixed to match GET /catalog/categories exactly
      // (verified against demo-catalog-subset.manifest.json
      // partNumberIngestPass.categories, Rotary Motion batch).
      'Rod End Bearings & Spherical Bearings',
    ],
  },
  {
    name: 'Rotary Power Transmission',
    categories: [
      'Chains & Sprockets',
      'Gears',
      'Keyless Bushings (Locking Fittings)',
      'Round Belts & Pulleys',
      'Shaft Couplings',
      'Tensioners',
      'Timing Belts & Pulleys',
      'V-Belts & Pulleys',
    ],
  },
  {
    name: 'Extrusions, Supports & Struts',
    categories: [
      'Aluminum Extrusions & Accessories',
      'Angle Plates',
      'Gussets',
      'Mounting Plates & Brackets',
      'Pipe Frame Components',
      'Struts, Strut Clamps & Stands',
      'Welded Standoffs & Metal Boxes',
    ],
  },
  {
    name: 'Fasteners & Hardware',
    categories: [
      'Screws & Bolts',
      'Set Screws',
      'Shoulder Screws',
      'Nuts',
      'Washers',
      'Metal Washers',
      'Threaded Inserts',
      'Threaded Rods & Studs',
      'Dowel, Stepped & Spring Pins',
      'Grease Fittings',
      'Magnets',
      'Spacers',
      'Metal Spacers',
      'Shims',
      'Standoffs',
      'Screws for Building Materials',
      'Hair Pins & Cotter Pins',
      'Machine Keys',
      'Retaining Rings & Snap Rings',
      // UID-419 Screws & Bolts batch (feat/pdp-category-captures) — exact
      // leaf names from GET /catalog/categories; 'Set Screws' and 'Shoulder
      // Screws' above are already listed and skipped here. Note: the real
      // seeded category is the single combined 'Eye Screws, Eye Bolts & Eye
      // Nuts' (comma-joined), not two separate categories.
      'Captive Panel Screws',
      'SEMS Screws with Pre-Assembled Washers',
      'Binding Head Machine Screws',
      'Flat Head Machine Screws',
      'Hex Head Upset Bolts',
      'Pan Head Machine Screws',
      'Round Countersunk Machine Screws',
      'Truss Head Machine Screws',
      'Eye Screws, Eye Bolts & Eye Nuts',
      'Fastener Accessories',
      'Hex Head Screws & Bolts',
      'Socket Flat Head Cap Screws',
      'Micro Screws & Precision Screws',
      'Plastic & Ceramic Screws',
      'Self-Drilling Screws',
      'Self-Tapping Screws',
      'Narrow Head Screws & Bolts',
      'Low-Profile Screws & Bolts',
      'Square Head Bolts & Carriage Bolts',
      'Tamper Resistant Screws',
      'U-Bolts',
      'Unified, Inch & Whitworth Screws',
      'Vented Screws',
      'Decorative Screws',
      'Thumb Screws',
      'Wing Screws',
    ],
  },
  {
    name: 'Locating & Positioning',
    categories: [
      'Adjusting Screws & Blocks',
      'Locating Guide Components',
      'Locating Pins & Bushings',
      'Locating Pins',
      'Locating Unit Components',
      'Plungers & Indexing Plungers',
      'Stop Pins & Stopper Blocks',
      'Toggle Clamps',
      'Workpiece Clamps',
    ],
  },
  {
    name: 'Springs & Shock Absorbers',
    categories: [
      'Compression Springs',
      'Extension Springs',
      'Torsion Springs',
      'Die Springs',
      'Gas Springs',
      'Shock Absorbers',
    ],
  },
  // UID-419 — new 8th top-level group added for the Materials PDP-capture
  // batch (feat/pdp-category-captures); not part of the original UID-253
  // prototype's 7 groups. Leaf names verified byte-for-byte against
  // demo-catalog-subset.manifest.json's partNumberIngestPass.categories
  // (Materials batch, 36 entries) in misumi-unified-apis.
  {
    name: 'Materials',
    categories: [
      'Ceramics',
      'Glass Plates & Mirrored Plates',
      'Mesh',
      'Metal Circular Plates',
      'Metal Cylinders & Rods',
      'Metal Hex Rods',
      'Metal Inclined Blocks',
      'Metal L-Shaped Angles',
      'Metal L-Shaped Blocks',
      'Metal Pipes',
      'Metal Plates & Blocks',
      'Metal T-Shaped Blocks',
      'Metal U-Shaped Blocks',
      'Metal U-Shaped Channels',
      'Cover Panels',
      'Expanded Metals',
      'Fences & Nets',
      'Painted Panels',
      'Perforated Metal Sheets',
      'Plywood Panels',
      'Resin Pipes',
      'Resin Rods',
      'Circular Resin Plates',
      'Resin Plates',
      'Thermal Insulation Materials',
      'L-Shaped Resin Covers',
      'Resin Cases',
      'Transparent Resin Plates & Cover Panels',
      'Transparent Resin Pipes',
      'Transparent Resin Rods',
      'Transparent Circular Resin Plates',
      'U-Shaped Resin Covers',
      'Adhesives & Mounting Components for Urethane',
      'Round Urethane, Rubber, Sponge & Felt',
      'Rubber & Rubber Sponge Cords',
      'Urethane, Rubber, Sponge & Felt Sheets',
    ],
  },
]

/** The clearly-labeled bucket for a real category that isn't in any group
 * above (`groupCategories.ts`) — never silently dropped. */
export const OTHER_GROUP_NAME = 'Other'

/**
 * UID-432 / ADR-0133 D3 -- the curated "Featured categories" set for the
 * `/catalog` browse landing's left `.catalog-bottom-row` cell
 * (`featuredCategoryCards.ts`). A frontend-only, explicitly named constant
 * (ADR-0091 D1) -- NOT `.slice(0, 4)` of `CATALOG_TAXONOMY_GROUPS` above,
 * because that would silently couple an editorial "what's featured" choice
 * to that array's incidental ordering (ADR-0133 D3b). Names are taxonomy
 * GROUP names (see `CatalogTaxonomyGroup.name`), not leaf category names.
 *
 * Sourced verbatim from the design source of truth's
 * `design_handoff_misumi_unified_platform/Fictiv Platform.dc.html`
 * (~line 10105's `featuredCategories` producer, `CAT.groups.slice(0, 4)`) --
 * these are that mock's first four taxonomy groups, in display order. Any
 * future re-curation is a product/editorial change to this list, not a code
 * shape change; see `catalogTaxonomy.test.ts`'s guard test asserting every
 * entry here still resolves against `CATALOG_TAXONOMY_GROUPS`.
 */
export const FEATURED_CATEGORY_GROUP_NAMES: readonly string[] = [
  'Linear Motion',
  'Rotary Motion',
  'Fasteners & Hardware',
  'Locating & Positioning',
]

/**
 * The parent taxonomy group name for one leaf category (e.g. `'Linear
 * Shafts'` → `'Linear Motion'`), for surfaces that show a category's parent
 * inline (the Home carousel's "N series · <Parent Category>" subtitle) rather
 * than grouping categories under group headers the way `ShopByCategory`
 * does. `null` when the category isn't listed in any group — the same
 * `OTHER_GROUP_NAME` case `groupCategories.ts` buckets separately; this
 * lookup deliberately does NOT fall back to `OTHER_GROUP_NAME` itself, so a
 * caller can choose to omit the subtitle's parent clause entirely rather
 * than show the literal word "Other".
 */
export function categoryParentGroupName(
  category: string,
  taxonomy: readonly CatalogTaxonomyGroup[] = CATALOG_TAXONOMY_GROUPS,
): string | null {
  for (const group of taxonomy) {
    if (group.categories.includes(category)) return group.name
  }
  return null
}

/**
 * 2026-09-03 "series-card + drawer configurator" pilot — the ONLY leaf
 * categories that trade `CatalogSeriesConfigurePage`'s flat SKU-line-item
 * `<Table>` for the legacy card-grid + `ConfiguratorDrawer` layout
 * (`SeriesCard`/`ResultCard`-style cards, click-to-configure). Both names
 * are real leaf categories already listed under 'Locating & Positioning'
 * above.
 *
 * A FRONTEND-ONLY, explicitly-named allowlist (same shape/rationale as
 * `FEATURED_CATEGORY_GROUP_NAMES` above) — never a computed slice of the
 * taxonomy, so this stays a deliberate, reviewable product/scope decision
 * per category rather than an accidental side effect of taxonomy reordering.
 * Every OTHER category, including 'Linear Shafts' (which has genuine
 * per-SKU line items that don't fit the series-card model), keeps the
 * existing table behavior — this is an opt-IN allowlist, not an opt-out.
 */
export const CARD_CONFIGURATOR_CATEGORIES: ReadonlySet<string> = new Set([
  'Locating Pins',
  'Locating Pins & Bushings',
])

/** Whether `category` (a `CatalogSeriesConfigurePage` route param, exact
 * match against `GET /catalog/categories`) uses the card-grid + drawer
 * configurator layout instead of the default flat-table layout. See
 * {@link CARD_CONFIGURATOR_CATEGORIES}. */
export function usesCardConfiguratorLayout(category: string): boolean {
  return CARD_CONFIGURATOR_CATEGORIES.has(category)
}
